import type { ColumnType } from 'antd/es/table';

import { sortByTime } from 'utils/antd/sorters';
import { formatDate } from 'utils/dayjs';

import type { PayoutReports } from 'api';

import type { FindAccountByAccountNumber } from 'hooks/context/useVirtualAccounts';

import { ColumnTitle } from '@atoms/ColumnTitle';
import { DownloadThinIcon } from '@atoms/Icon/DownloadThinIcon';
import { StatusTag } from '@atoms/StatusTag';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

import { PAYOUT_STATUSES } from 'components/pages/Payouts/constants';

export const getColumns = (
  findAccountByAccountNumber: FindAccountByAccountNumber,
  downloadReport: (uuid: string) => void
): ColumnType<PayoutReports>[] => {
  return [
    {
      key: 'empty_space',
      width: 50
    },
    {
      title: <ColumnTitle title="From" />,
      dataIndex: 'created_after',
      key: 'created_after',
      render: (createdAfter: string) => (
        <div className="text-lg">{formatDate(createdAfter)}</div>
      )
    },
    {
      title: <ColumnTitle title="To" />,
      dataIndex: 'created_before',
      key: 'created_before',
      render: (createdBefore: string) => (
        <div className="text-lg">{formatDate(createdBefore)}</div>
      )
    },
    {
      title: <ColumnTitle title="Created date (UTC)" />,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt: string) => <FormattedDateTime value={createdAt} />,
      sorter: (a, b) => sortByTime(a.created_at, b.created_at),
      onHeaderCell: () => ({
        className: 'align-sorter-icon'
      })
    },
    {
      title: <ColumnTitle title="Account" />,
      dataIndex: 'virtual_account_number',
      key: 'virtual_account_number',
      align: 'right',
      render: (virtualAccountNumber: string) => {
        const account = findAccountByAccountNumber(virtualAccountNumber);

        return (
          <div>
            <p className="text-sm">{virtualAccountNumber}</p>
            <div className="font-semibold">
              {account?.name ?? 'All accounts'}
            </div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Status" />,
      dataIndex: 'state_categories',
      key: 'state_categories',
      align: 'right',
      render: (stateCategories: string[]) => {
        const status = stateCategories[0];
        return status ? (
          <StatusTag status={status} statuses={PAYOUT_STATUSES} />
        ) : (
          ''
        );
      }
    },
    {
      title: <ColumnTitle title="Action" />,
      dataIndex: 'uuid',
      key: 'uuid',
      align: 'center',
      render: (uuid: string) => {
        return (
          <div className="align-center flex h-full justify-center">
            <DownloadThinIcon
              className="cursor-pointer"
              fontSize="32px"
              onClick={() => downloadReport(uuid)}
            />
          </div>
        );
      }
    }
  ];
};
